;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/interview/:token\",\"regex\":\"^/interview/([^/]+)$\",\"paramNames\":[\"token\"]},{\"path\":\"/space/projects/:id\",\"regex\":\"^/space/projects/([^/]+)$\",\"paramNames\":[\"id\"]},{\"path\":\"/space/projects/:id/reports/:reportId\",\"regex\":\"^/space/projects/([^/]+)/reports/([^/]+)$\",\"paramNames\":[\"id\",\"reportId\"]},{\"path\":\"/space/projects/public/:projectId/reports/:reportId\",\"regex\":\"^/space/projects/public/([^/]+)/reports/([^/]+)$\",\"paramNames\":[\"projectId\",\"reportId\"]},{\"path\":\"/space/records/:id\",\"regex\":\"^/space/records/([^/]+)$\",\"paramNames\":[\"id\"]},{\"path\":\"/space/records/public/:id\",\"regex\":\"^/space/records/public/([^/]+)$\",\"paramNames\":[\"id\"]}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/livekit-egress\"},{\"path\":\"/login\"},{\"path\":\"/ref\"},{\"path\":\"/signup\"},{\"path\":\"/space\"},{\"path\":\"/space/admin/byok\"},{\"path\":\"/space/agent\"},{\"path\":\"/space/interviewer\"},{\"path\":\"/space/projects\"},{\"path\":\"/space/records\"},{\"path\":\"/space/records/without-project\"},{\"path\":\"/space/reports\"},{\"path\":\"/space/transcripts\"}]}";globalThis["_sentryNextJsVersion"] = "16.0.10";import * as Sentry from "@sentry/nextjs";

import { initializeAmplitude } from "@/lib/analytics/amplitude";
import {
  applyChunkLoadDiagnosticsToEvent,
  extractChunkLoadErrorInfo,
  installChunkLoadDiagnostics,
  prepareChunkLoadDiagnosticsForCapture,
} from "@/lib/chunk-load-diagnostics";
import { parseTracePropagationTargets } from "@/lib/sentry-trace-targets";

const isProduction =
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === "production";

function parseSampleRate(
  value: string | undefined,
  fallback: number
): number {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) {
    return parsed;
  }

  return fallback;
}

const tracesSampleRate = parseSampleRate(
  process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
  isProduction ? 0.05 : 1.0
);

const replaysSessionSampleRate = parseSampleRate(
  process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  isProduction ? 0.01 : 0.1
);

const replaysOnErrorSampleRate = parseSampleRate(
  process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  isProduction ? 0.2 : 1.0
);

function isTransportOrAuthNoise(message: string | null): boolean {
  if (!message) {
    return false;
  }

  const normalized = message.toLowerCase();
  return (
    normalized.includes("abortincoming") ||
    normalized.includes("econnreset") ||
    normalized.includes("connection reset by peer") ||
    normalized.includes("auth_unverified") ||
    normalized.includes("auth_expired") ||
    normalized.includes("auth_invalid") ||
    normalized.includes("projectsapiautherror") ||
    normalized.includes("projects api authentication error")
  );
}

function shouldIgnoreClientEvent(event: Record<string, unknown>): boolean {
  const telemetrySignal =
    event.tags && typeof event.tags === "object"
      ? (event.tags as Record<string, unknown>).telemetry_signal
      : undefined;

  if (telemetrySignal === "auth_transport_failure") {
    return false;
  }

  const candidateMessages = [
    event.message,
    (event.exception as { values?: Array<{ value?: unknown }> } | undefined)
      ?.values
      ?.map((value) => (typeof value === "object" ? value.value : undefined))
      .filter((value): value is string => typeof value === "string")
      .join(" "),
  ]
    .filter((message): message is string => typeof message === "string")
    .join(" ");

  return isTransportOrAuthNoise(candidateMessages);
}

function shouldIgnoreClientNoise(message: string | null): boolean {
  if (!message) {
    return false;
  }

  return (
    message.includes("ResizeObserver loop") ||
    message.includes("ResizeObserver loop limit") ||
    message.includes("Cross-Origin-Opener-Policy policy would block the window.closed call.") ||
    message.includes("window.closed")
  );
}

if (typeof window !== "undefined") {
  installChunkLoadDiagnostics();
  initializeAmplitude();
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  enabled: process.env.NEXT_PUBLIC_IS_SENTRY_ENABLED === "true",
  environment:
    process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
  release: process.env.NEXT_PUBLIC_APP_VERSION,

  tracesSampleRate,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,

  tracePropagationTargets: parseTracePropagationTargets(
    process.env.NEXT_PUBLIC_TRACE_TARGETS
  ),

  debug: false,

  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  async beforeSend(event, hint) {
    const userAgent = event.request?.headers?.["User-Agent"];
    if (userAgent && /bot|crawler|spider/i.test(userAgent)) {
      return null;
    }

    if (shouldIgnoreClientEvent(event as unknown as Record<string, unknown>)) {
      return null;
    }

    const originalException = hint.originalException;
    const originalMessage =
      originalException instanceof Error
        ? originalException.message
        : typeof originalException === "string"
          ? originalException
          : null;

    if (shouldIgnoreClientNoise(originalMessage)) {
      return null;
    }

    const exceptionMessage = event.exception?.values
      ?.map((value) => value.value)
      .find((value): value is string => typeof value === "string");

    if (shouldIgnoreClientNoise(exceptionMessage ?? null)) {
      return null;
    }

    const chunkLoadCandidate = hint.originalException ?? event;
    if (extractChunkLoadErrorInfo(chunkLoadCandidate)) {
      await prepareChunkLoadDiagnosticsForCapture();
      applyChunkLoadDiagnosticsToEvent(event, chunkLoadCandidate);
    }

    return event;
  },
});
